// src/pages/MapPage.jsx

import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useParams, useSearch } from '@tanstack/react-router'
import { useAppNav } from '../hooks/useAppNav'

import MapView from '../components/MapView.jsx'
import HeliosDock from '../components/HeliosDock.jsx'
import LayerPanel from '../components/LayerPanel/index.jsx'
import SiteIDPanel from '../components/siteid/SiteIDPanel'
import WellInfoPanel from '../components/WellInfoPanel'
import WellFinderPanel from '../components/wellfinder/WellFinderPanel'
import PurchasePanel from '../components/purchase/PurchasePanel'
import AoiPanel from '../components/AoiPanel.jsx'
import AoiToolbar from '../components/AoiToolbar.jsx'
// import ModeRail from '../components/ModeRail.jsx' // commented out — see render below
import CollaborationRail from '../components/collaboration/CollaborationRail.jsx'

import { applyMapContext } from '../map/MapContextApplier'
import { useProjectStore } from '../stores/useProjectStore'
import { useLayerRegistryStore } from '../stores/useLayerRegistryStore'
import { useAoiStore } from '../stores/useAoiStore'
import { useWellSelectionStore } from '../stores/useWellSelectionStore'
import { useOperatingAoiContext } from '../stores/useOperatingAoiContext'
import { useUserLayersStore } from '../stores/useUserLayersStore'
import { useLayerSetsStore } from '../stores/useLayerSetsStore'
import { useMapActions } from '../stores/useMapActions'
import { useAppViewStore } from '../stores/useAppViewStore'
import { useLastSessionStore } from '../stores/useLastSessionStore'
import { useLayersStore } from '../stores/useLayersStore'
import AoiCoverageHud from '../components/AoiCoverageHud.jsx'
import AIOnDemandPanel from '../components/aiondemand/AIOnDemandPanel.jsx'
import { useAIOnDemandStore } from '../stores/useAIOnDemandStore'

import AnnotationPanel from '../components/annotations/AnnotationPanel.jsx'
import { useAnnotationStore } from '../stores/useAnnotationStore'

import { fetchWithAuth } from '../api/fetchWithAuth'
import { fetchMapView } from '../api/mapViewsApi'

import { aoiBoundsFlat, combinedAoiBounds } from '../utils/aoiBounds';

import PadRekkoPanel from '../components/padrekko/PadRekkoPanel.jsx'
import PadRekkoDashboard from '../components/padrekko/qc/PadRekkoDashboard.jsx'
import CaseBuilderPanel from '../components/padrekko/qc/builder/CaseBuilderPanel.jsx'
import QcCaseBar from '../components/padrekko/qc/QcCaseBar.jsx'
import CaseBuilderMap from '../components/padrekko/qc/builder/CaseBuilderMap.jsx'
import { useCaseBuilderStore } from '../stores/useCaseBuilderStore'
import { useQcStore } from '../stores/useQcStore'
import { usePadStore } from '../stores/usePadStore'
import { useAuthStore, selectIsRestrictedOperator } from '../stores/useAuthStore'
import { useCollabRailStore, COLLAB_RAIL_WIDTH } from '../stores/useCollabRailStore'

import JobResultLayers from '../components/aiondemand/JobResultLayers.jsx';

import Well3DSurface from '../components/well3d/Well3DSurface';
import { useWellFinderStore } from '../stores/useWellFinderStore';

export default function MapPage() {

  const projectId = useProjectStore((s) => s.activeProjectId);

  // strict:false rather than from:'/map/$tab'. `from` takes a route ID, and
  // route IDs are derived from the tree — adding the pathless _platform layout
  // re-keyed this one to '/_platform/map/$tab' and the page threw on load while
  // the path still routed and every test passed.
  //
  // Naming the new ID would be correct today and a landmine for whoever adds
  // the next layout route. strict:false does not re-break, and it matches the
  // eight other call sites in ui/src including useAppNav, which reads the same
  // `tab` param this way. Both consumers below already tolerate undefined
  // (`search?.mapMode ?? 'layers'`, `activeTab ?? 'explore'`), so the looser
  // typing costs nothing here.
  //
  // Guarded by src/router.byIdReferences.test.jsx.
  const { tab: activeTab } = useParams({ strict: false });
  const search = useSearch({ strict: false });
  const mapMode = search?.mapMode ?? 'layers';
  const activeProduct = activeTab ?? 'explore';
  const isMapProduct = activeProduct === 'explore';

  // PadRekko project-landing dashboard gate. Shown full-bleed (in place of the
  // rail+map) when: PadRekko product, the QC tab is active, no case is loaded,
  // and the viewer is not a restricted operator (operators land direct on their
  // case via useRestrictedCaseSelection). The Volumetrics tab and the loaded-
  // case workspace both fall through to the normal rail+map below, untouched.
  const padrekkoTab = usePadStore((s) => s.padrekkoTab);
  const qcSession = useQcStore((s) => s.session);
  const isRestrictedOperator = useAuthStore(selectIsRestrictedOperator);
  const showPadrekkoDashboard =
    activeProduct === 'padrekko' &&
    padrekkoTab === 'qc' &&
    !qcSession &&
    !isRestrictedOperator &&
    // A ?caseId deep-link must fall through so ReclamationQcTab mounts and
    // loads it (the dashboard early-return would otherwise unmount the loader).
    !search?.caseId;

  // When the collaboration rail opens, the dashboard overlay yields its right
  // edge so the rail (a flex sibling on the far right) is uncovered.
  const collabRailOpen = useCollabRailStore((s) => s.open);

  const { syncViewport, setMapMode } = useAppNav();

  const mapRef = useRef(null)
  const [drawInstance, setDrawInstance] = useState(null);
  const [drawVersion, setDrawVersion] = useState(0);
  const publishDrawInstance = useAppViewStore((s) => s.setDrawInstance);

  const selectedWell = useWellSelectionStore((s) => s.selected);
  const clearWell = useWellSelectionStore((s) => s.clearWell);

  const registerLayers = useLayerRegistryStore((s) => s.registerLayers);
  const clearProjectScopedLayers = useLayerRegistryStore(
    (s) => s.clearProjectScopedLayers
  );
  const setLayerVisibilityBatch = useLayerRegistryStore(
    (s) => s.setLayerVisibilityBatch
  );

  const [bootstrapReady, setBootstrapReady] = useState(false);

  const loadAois = useAoiStore((s) => s.loadAois);
  const resetAois = useAoiStore((s) => s.resetForProjectChange);
  const exitAoiPolygonEdit = useAoiStore((s) => s.exitAoiPolygonEdit);
  const aois = useAoiStore(s => s.aois);
  const activeAoiId = useAoiStore(s => s.activeAoiId);
  const selectedAoiId = useAoiStore(s => s.selectedAoiId);
  const coverageByAoi = useAoiStore(s => s.imageryCoverageByAoi);
  const coverageLoading = useAoiStore(s => s.imageryCoverageLoading);
  const loadCoverage = useAoiStore(s => s.loadImageryCoverage);

  const activeOperatingAoiId = useOperatingAoiContext(
    (s) => s.activeOperatingAoiId
  );

  const loadUserLayers = useUserLayersStore((s) => s.loadUserLayers);
  const resetUserLayers = useUserLayersStore((s) => s.reset);

  const loadLayerSets = useLayerSetsStore((s) => s.loadSets);
  const restoreDefaultLayerSet = useLayerSetsStore((s) => s.restoreDefault);
  const resetLayerSets = useLayerSetsStore((s) => s.reset);
  const beginLayerSetRestore = useLayerSetsStore((s) => s.beginRestore);
  const endLayerSetRestore = useLayerSetsStore((s) => s.endRestore);

  const zoomToBounds = useMapActions((s) => s.zoomToBounds);

  const rebuildGroups = useLayersStore((s) => s.rebuildGroups);
  const resetFeatureTypes = useLayersStore((s) => s.setActiveFeatureTypes);

  const role = useProjectStore((s) => s.role);

  const snapshotSession = useLastSessionStore((s) => s.snapshotSession);
  const [mapReady, setMapReady] = useState(false);
  const [viewRestored, setViewRestored] = useState(false);

  const annotationOpen = useAnnotationStore(s => s.open);
  const annotationTool = useAnnotationStore(s => s.tool);

  const aiPreviewLayers = useAIOnDemandStore((s) => s.previewLayers);

  const well3dSurfaceRef = useRef(null);
  const wellFinderTab = useWellFinderStore(s => s.activeTab);
  const well3dActive = activeProduct === 'well_finder' && wellFinderTab === 'pro';

  /* ---------------------------------------------------- */
  /* LAST-SESSION SNAPSHOT                                */
  /* ---------------------------------------------------- */

  useEffect(() => {
    snapshotSession(projectId, activeTab);
  }, [projectId, activeTab, snapshotSession]);

  /* ---------------------------------------------------- */
  /* SHARED VIEW RESTORE                                  */
  /* Detects ?view= param and applies saved map state     */
  /* once the map is ready.                               */
  /* ---------------------------------------------------- */

  useEffect(() => {
    if (!mapReady || viewRestored) return;
    const params = new URLSearchParams(window.location.search);
    const viewId = params.get("view");
    if (!viewId) return;

    setViewRestored(true);

    fetchMapView(viewId)
      .then((view) => {
        if (!view?.context) return;
        // Inject into the pending context mechanism MapPage already supports
        window.__pendingMapContext = { ...view.context, v: 1 };
        applyMapContext({
          map: mapRef.current,
          context: view.context,
          stores: {
            aoi: useAoiStore.getState(),
            layers: {
              visibleById: Object.fromEntries(
                Object.entries(useLayerRegistryStore.getState().layers || {})
                  .map(([id, l]) => [id, l.visible])
              ),
              toggleLayer: (id) => useLayerRegistryStore.getState().toggleLayerVisibility(id),
            },
          },
        });
      })
      .catch((err) => console.warn("[MapPage] view restore failed", err));
  }, [mapReady, viewRestored]);

  /* ---------------------------------------------------- */
  /* PRODUCT → LAYERS                                     */
  /* ---------------------------------------------------- */

  const PRODUCT_LAYERS = useMemo(() => ({
    siteid: ["siteid-polygons", "siteid-polygons-outline", "siteid-lines", "siteid-points"],
    well_finder: ["wells"],
    padrekko: [],
    explore: [],
  }), [])

  /* ---------------------------------------------------- */
  /* PROJECT BOOTSTRAP                                    */
  /* ---------------------------------------------------- */

  useEffect(() => {
    if (!projectId || projectId === "global") return;

    let cancelled = false;

    setBootstrapReady(false);

    // CLOSE THE RESTORE BARRIER SYNCHRONOUSLY, HERE, not inside bootstrap().
    // Anything that sets layer state between now and applySnapshot will be
    // reconciled away by it, and the window has to be closed before the first
    // await, not after. A product layer that mounts in this window (WellFinder
    // turning the RRC surface wells on) waits on this rather than racing it.
    beginLayerSetRestore();

    async function bootstrap() {
      try {
        clearProjectScopedLayers();
        resetUserLayers();
        resetLayerSets();
        resetAois();
        // FEATURES-tab toggles aren't registry/persisted, so they'd otherwise
        // linger across projects. Clear to a blank slate; the default layer set
        // (restored below) layers its own features back on.
        resetFeatureTypes({});

        const res = await fetchWithAuth(`/api/v1/${projectId}/map-bootstrap`);

        if (!res.ok) {
          const text = await res.text();
          console.error("BOOTSTRAP ERROR BODY:", text);
          throw new Error("Failed to load map bootstrap");
        }

        const data = await res.json();
        if (cancelled) return;

        if (data.project) {
          useProjectStore.getState().setActiveProject({
            ...useProjectStore.getState().activeProject,
            ...data.project,
          });
        }

        await loadAois(projectId);

        const rawAnalysis = data.layers?.analysis ?? [];

        const flattenedAnalysis = rawAnalysis.flatMap((run) => {
          const runLayer = {
            ...run,
            visible: false,
            default_visible: false,
            source: null,
            children: (run.children ?? []).map((c) => c.id),
          };

          const childLayers = (run.children ?? []).map((c) => ({
            ...c,
            kind: "analysis-detection",
            parent_id: run.id,
            scope: run.scope ?? "project",
            visible: false,
            default_visible: c.default_visible ?? false,
          }));

          return [runLayer, ...childLayers];
        });

        const allLayers = [
          ...(data.layers?.imagery ?? []),
          ...flattenedAnalysis,
          ...(data.layers?.custom ?? []),
          ...(data.layers?.operational ?? []),
        ];

        registerLayers(allLayers, { replaceScope: "project" });

        const imageryLayers = data.layers?.imagery ?? [];
        rebuildGroups({ imageryLayers, userRole: role ?? "viewer" });

        if (cancelled) return;

        await loadUserLayers(projectId);

        await loadLayerSets(projectId);
        if (!cancelled) {
          await restoreDefaultLayerSet(projectId);
          setBootstrapReady(true);
        }
      } catch (err) {
        console.error("[MAP PAGE] bootstrap failed", err);
        // restoreDefault releases the barrier itself, but a failure ABOVE it
        // never reaches that call. Release here so a failed bootstrap degrades
        // to "no default set applied" instead of leaving every consumer of the
        // barrier waiting on a restore that will never run.
        endLayerSetRestore();
      }
    }

    bootstrap();

    return () => {
      cancelled = true;
    };
  }, [
    projectId,
    clearProjectScopedLayers,
    resetUserLayers,
    resetLayerSets,
    resetAois,
    resetFeatureTypes,
    loadAois,
    registerLayers,
    rebuildGroups,
    loadUserLayers,
    loadLayerSets,
    restoreDefaultLayerSet,
    beginLayerSetRestore,
    endLayerSetRestore,
  ]);

  /* ---------------------------------------------------- */
  /* AUTO-ZOOM ON PROJECT LOAD                            */
  /* ---------------------------------------------------- */

  const [zoomedForProject, setZoomedForProject] = useState(null);

  useEffect(() => {
    if (!bootstrapReady) return;
    if (!zoomToBounds) return;
    if (!aois.length) return;

    setZoomedForProject(projectId);

    if (selectedAoiId) {
      const selected = aois.find(a => a.id === selectedAoiId);
      const bounds = selected ? aoiBoundsFlat(selected.geom) : null;
      if (bounds) { zoomToBounds(bounds); return; }
    }

    const combined = combinedAoiBounds(aois);
    if (combined) zoomToBounds(combined);

  }, [bootstrapReady, aois, selectedAoiId, zoomToBounds, projectId]);

  /* ---------------------------------------------------- */
  /* PRODUCT → LAYER VISIBILITY                           */
  /* ---------------------------------------------------- */

  useEffect(() => {
    const base = PRODUCT_LAYERS[activeProduct] || [];
    const extras = activeProduct === 'helios' ? aiPreviewLayers : [];
    const layers = [...base, ...extras];

    if (!layers.length) return;
    setLayerVisibilityBatch(layers, true);
  }, [activeProduct, aiPreviewLayers, PRODUCT_LAYERS, setLayerVisibilityBatch]);

  const previouslyShownRef = useRef([]);
  useEffect(() => {
    const currentPreview = activeProduct === 'helios' ? aiPreviewLayers : [];

    const toShow = currentPreview.filter(
      (id) => !previouslyShownRef.current.includes(id)
    );
    if (toShow.length) setLayerVisibilityBatch(toShow, true);

    const baseLayers = PRODUCT_LAYERS[activeProduct] || [];
    const toHide = previouslyShownRef.current.filter(
      (id) => !currentPreview.includes(id) && !baseLayers.includes(id)
    );
    if (toHide.length) setLayerVisibilityBatch(toHide, false);

    previouslyShownRef.current = currentPreview;
  }, [activeProduct, aiPreviewLayers, PRODUCT_LAYERS, setLayerVisibilityBatch]);

  /* ---------------------------------------------------- */
  /* AOI EDIT CONTEXT — tear down on leave                */
  /* Covers both a mode switch (aoi → layers) and tab/    */
  /* product navigation (explore → anything). aoiPolygon- */
  /* EditMode is a store flag that otherwise persists and */
  /* re-arms MapView's vertex edit when AOI mode is later */
  /* re-entered — clear it alongside the draw instance.   */
  /* ---------------------------------------------------- */

  const aoiEditActive = isMapProduct && mapMode === "aoi";

  /* ---------------------------------------------------- */
  /* PADREKKO CASE BUILDER — enter/leave + unsaved guard  */
  /* ---------------------------------------------------- */

  const padrekkoBuilderActive = isMapProduct && mapMode === "padrekko_builder";
  const builderStart = useCaseBuilderStore((s) => s.start);
  const builderExit = useCaseBuilderStore((s) => s.exit);
  const builderPadCount = useCaseBuilderStore((s) => s.pads.length);

  useEffect(() => {
    if (padrekkoBuilderActive) builderStart(search?.returnTo ?? "padrekko");
    else builderExit();
  }, [padrekkoBuilderActive, search?.returnTo, builderStart, builderExit]);

  // Warn before a hard refresh / tab close while a draft has pads (the
  // localStorage mirror still recovers it, but the prompt avoids surprise).
  useEffect(() => {
    if (!(padrekkoBuilderActive && builderPadCount > 0)) return;
    const onBeforeUnload = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [padrekkoBuilderActive, builderPadCount]);

  useEffect(() => {
    if (aoiEditActive) return;
    if (drawInstance) {
      drawInstance.stopDrawing?.();
      drawInstance.delete?.();
      drawInstance.lastFeature = null;
    }
    exitAoiPolygonEdit();
  }, [aoiEditActive, drawInstance, exitAoiPolygonEdit]);

  // Belt-and-suspenders: clear the polygon-edit flag if MapPage unmounts
  // entirely (route away from /map) so it can't leak into a later mount.
  useEffect(() => () => exitAoiPolygonEdit(), [exitAoiPolygonEdit]);

  /* ---------------------------------------------------- */
  /* LOAD COVERAGE HUD                                    */
  /* ---------------------------------------------------- */

  useEffect(() => {
    if (mapMode === "aoi" && activeAoiId) {
      loadCoverage(activeAoiId);
    }
  }, [mapMode, activeAoiId, loadCoverage]);

  /* ---------------------------------------------------- */
  /* MAP READY                                            */
  /* ---------------------------------------------------- */

  function handleMapReady(map) {
    mapRef.current = map;
    setMapReady(true);

    map.on('moveend', () => {
      const c = map.getCenter()
      syncViewport({ zoom: map.getZoom(), lat: c.lat, lng: c.lng })
    })

    const ctx = window.__pendingMapContext;
    if (!ctx || ctx.v !== 1) return;
    applyMapContext({ map, context: ctx, stores: { aoi: useAoiStore.getState() } });
    window.__pendingMapContext = null;
  }

  function handleDrawReady(draw) {
    draw.onUpdate = () => setDrawVersion((v) => v + 1);
    setDrawInstance(draw);
    // Also publish to the global view store so deep-tree consumers (e.g.
    // the Padrekko QC polygon drawer) can grab it without prop-drilling.
    publishDrawInstance(draw);
  }

  /* ---------------------------------------------------- */
  /* RENDER                                               */
  /* ---------------------------------------------------- */

  return (
    // `isolate` makes MapPage its own stacking context so its internal map
    // chrome (CommandRail ruler/flyto + minimap at z-[80], and the dashboard
    // overlay below) can't compete with app-shell UI. Without it the dashboard
    // overlay's z would also cover the TopNav project dropdown (z-50); isolated,
    // that dropdown — being outside MapPage — paints above the whole map area.
    <div className="h-full w-full flex bg-bg overflow-hidden relative isolate">

      {/* PadRekko project dashboard — full-bleed OVERLAY rather than an early
          return, so MapView (and its map instance) stays mounted. Closing a
          case unmounts the QC map-layers whose cleanup calls map.getLayer();
          tearing the map down underneath them throws. The opaque overlay keeps
          the map alive (warm) while covering the rail+map. */}
      {showPadrekkoDashboard && (
        // z above the map chrome rendered inside MapView (CommandRail ruler/
        // flyto + minimap top out at z-[80]) so the dashboard fully covers it.
        // Right edge retreats by the rail width when the collab rail is open,
        // so the rail (flex sibling on the far right) is revealed and the
        // dashboard collapses into the remaining space.
        <div
          className="absolute inset-y-0 left-0 z-[90] bg-bg transition-[right] duration-300 ease-in-out"
          style={{ right: collabRailOpen ? COLLAB_RAIL_WIDTH : 0 }}
        >
          <PadRekkoDashboard />
        </div>
      )}

      {/* LEFT PANEL */}
      <section
        className="border-r border-slate-800 bg-bg-softer flex flex-row"
        style={{ width: "calc(20rem + 50px)" }}
        data-layer-panel
      >
        {/* ModeRail commented out — entry to AOI mode now lives in the AOIs
            tab's "New" button. NOTE: there is currently no path back to
            mapMode="layers" without this rail. */}
        {/* {isMapProduct && (
          <ModeRail activeMode={mapMode} onModeChange={setMapMode} />
        )} */}

        <div className="flex-1 relative overflow-hidden">

          <Panel visible={isMapProduct && mapMode === "layers"}>
            <LayerPanel />
          </Panel>

          <Panel visible={isMapProduct && mapMode === "aoi"}>
            <AoiPanel
              drawInstance={drawInstance}
              drawVersion={drawVersion}
            />
          </Panel>

          <Panel visible={activeProduct === "siteid"}>
            <SiteIDPanel />
          </Panel>

          <Panel visible={activeProduct === "well_finder"}>
            <WellFinderPanel mapRef={mapRef} mapReady={mapReady} well3dSurfaceRef={well3dSurfaceRef} />
          </Panel>

          <Panel visible={activeProduct === "padrekko"}>
            <PadRekkoPanel />
          </Panel>

          <Panel visible={padrekkoBuilderActive}>
            <CaseBuilderPanel />
          </Panel>

          <Panel visible={activeProduct === "purchase"}>
            <PurchasePanel />
          </Panel>

          <Panel visible={activeProduct === "helios"}>
            <AIOnDemandPanel mapRef={mapRef} />
          </Panel>

        </div>
      </section>

      {/* MAP AREA — CollaborationRail lives here as an absolute overlay
          so the map background shows through behind the tab handle.
          A COLUMN: the PadRekko case bar on top (null, reserving nothing, when
          no QC case is open), then the map area. The bar PUSHES the map down
          rather than overlaying it: everything that was positioned against
          this section is now positioned against the wrapper below, which is
          exactly the map's area, so top-anchored map controls move down with
          the map and MapLibre resizes itself (trackResize). */}
      <section className="flex-1 flex flex-col bg-bg-soft min-w-0">
        {activeProduct === "padrekko" && padrekkoTab === "qc" && !padrekkoBuilderActive && (
          <QcCaseBar />
        )}
        <div className="relative flex-1 min-h-0">
        <div className={well3dActive ? 'invisible' : ''}>
          <MapView
            mapRef={mapRef}
            onMapReady={handleMapReady}
            onDrawReady={handleDrawReady}
            aoiModeActive={isMapProduct && mapMode === "aoi"}
            annotationActive={annotationOpen}
            annotationTool={annotationTool}
          />
        </div>
        {isMapProduct && mapMode === "aoi" && drawInstance && (
          <div className="absolute top-2 left-3 z-30 pointer-events-auto">
            <AoiToolbar draw={drawInstance} version={drawVersion} />
          </div>
        )}

        {isMapProduct && mapMode === "aoi" && activeAoiId && (
          <AoiCoverageHud
            aoiName={aois.find(a => a.id === activeAoiId)?.name ?? "AOI"}
            coverage={coverageByAoi[activeAoiId]}
            loading={coverageLoading[activeAoiId] ?? false}
          />
        )}

        {padrekkoBuilderActive && (
          <CaseBuilderMap mapRef={mapRef} mapReady={mapReady} />
        )}

        {selectedWell && (
          <WellInfoPanel well={selectedWell} onClose={clearWell} />
        )}

        {/*
        <HeliosDock mapRef={mapRef} fetchWithAuth={fetchWithAuth} panelOpen={!!selectedWell} />
        */}

        {activeProduct === 'helios' && (
          <JobResultLayers mapRef={mapRef} mapLoaded={mapReady} />
        )}

        {well3dActive && (
          <Well3DSurface ref={well3dSurfaceRef} />
        )}

        <AnnotationPanel />

        </div>
      </section>

      {/* COLLABORATION RAIL — flex sibling so it pushes the map left when open */}
      <CollaborationRail />

    </div>
  );
}

/* ---------------------------------------------------- */
/* PANEL WRAPPER                                        */
/* ---------------------------------------------------- */

function Panel({ visible, children }) {
  return (
    <div
      className={`absolute inset-0 transition-all duration-200 ease-out ${visible
        ? "opacity-100 translate-x-0"
        : "opacity-0 translate-x-2 pointer-events-none"
        }`}
    >
      {children}
    </div>
  );
}
